import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DownloadIcon from "@mui/icons-material/Download";
import {
    Box, Button, Container, Paper, Stack, TextField, Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

const VisuallyHiddenInput = styled("input")({
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    height: 1,
    overflow: "hidden",
    position: "absolute",
    bottom: 0,
    left: 0,
    whiteSpace: "nowrap",
    width: 1,
});

/**
 * Converts an SVG die-line file to an overlay version:
 * - Removes width/height attributes (keeps viewBox only)
 * - Changes all strokes to white
 * - Sets stroke-width to 0.5
 * - Removes the border/frame path (Black stroke)
 * - Adds stroke-linecap and stroke-linejoin
 */
function convertToOverlay(svgText) {
    // Parse with DOMParser
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgText, "image/svg+xml");
    const svg = doc.querySelector("svg");
    if (!svg) throw new Error("No <svg> element found");

    // Remove width and height, keep viewBox
    svg.removeAttribute("width");
    svg.removeAttribute("height");

    // Process all paths
    const paths = svg.querySelectorAll("path");
    const kept = [];
    for (const path of paths) {
        const stroke = (path.getAttribute("stroke") || "").toLowerCase();
        // Remove border path (Black stroke)
        if (stroke === "black") continue;
        // Convert to white overlay
        path.setAttribute("stroke", "white");
        path.setAttribute("stroke-width", "0.5");
        path.setAttribute("stroke-linecap", "round");
        path.setAttribute("stroke-linejoin", "round");
        kept.push(path);
    }

    if (kept.length === 0) throw new Error("No die-line paths found (only border path)");

    // Rebuild SVG with only kept paths
    // Remove all children
    while (svg.firstChild) svg.removeChild(svg.firstChild);
    for (const p of kept) svg.appendChild(p);

    // Serialize
    const serializer = new XMLSerializer();
    return serializer.serializeToString(svg);
}

export default function SvgOverlayConverterPage() {
    const navigate = useNavigate();
    const [svgFile, setSvgFile] = useState(null);
    const [result, setResult] = useState("");
    const [error, setError] = useState(null);
    const fileRef = useRef(null);

    const handleFileChange = (e) => {
        const file = e.target.files?.[0] ?? null;
        setSvgFile(file);
        setError(null);
        setResult("");
    };

    const handleConvert = async () => {
        if (!svgFile) {
            setError("Select an SVG file first.");
            return;
        }
        setError(null);
        try {
            const svgText = await svgFile.text();
            const overlay = convertToOverlay(svgText);
            setResult(overlay);
        } catch (e) {
            setError(e.message || "Conversion failed");
            setResult("");
        }
    };

    const handleDownload = () => {
        if (!result) return;
        const blob = new Blob([result], { type: "image/svg+xml" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "overlay.svg";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    return (
        <Container maxWidth="md" sx={{ py: 3 }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/admin/system/anything")} sx={{ mb: 2 }}>
                Back
            </Button>
            <Typography variant="h5" sx={{ mb: 3 }}>Convert SVG to Overlay</Typography>

            <Paper sx={{ p: 3 }}>
                <Stack spacing={3}>
                    <Box>
                        <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>SVG File</Typography>
                        <Button
                            component="label"
                            variant="outlined"
                            startIcon={<CloudUploadIcon />}
                            size="small"
                        >
                            Choose SVG file
                            <VisuallyHiddenInput
                                ref={fileRef}
                                type="file"
                                accept=".svg,image/svg+xml"
                                onChange={handleFileChange}
                            />
                        </Button>
                        {svgFile && (
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                                {svgFile.name} ({(svgFile.size / 1024).toFixed(1)} KB)
                            </Typography>
                        )}
                    </Box>

                    <Stack direction="row" spacing={2}>
                        <Button variant="contained" onClick={handleConvert} disabled={!svgFile}>
                            Convert
                        </Button>
                        {result && (
                            <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownload}>
                                Download overlay.svg
                            </Button>
                        )}
                    </Stack>

                    {error && <Typography color="error">{error}</Typography>}

                    {result && (
                        <Box>
                            <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 600 }}>Result</Typography>
                            <TextField
                                multiline
                                fullWidth
                                value={result}
                                minRows={6}
                                maxRows={20}
                                InputProps={{ readOnly: true, sx: { fontFamily: "monospace", fontSize: 11 } }}
                            />
                        </Box>
                    )}
                </Stack>
            </Paper>
        </Container>
    );
}
